import React, { useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from './Layout';
import PostAuthGate from './PostAuthGate';
import { useSeo } from './Seo';
import { useAuth } from '../contexts/AuthContext';

/*
 * What a signed-out visitor gets instead of a members-only page.
 *
 * It replaces a redirect to /login. The redirect worked, but it threw away
 * everything the visitor had: the navigation disappeared, the page they asked
 * for was never named, and the only thing on screen was an email box. Someone
 * who clicked "Labs" out of curiosity had no idea what they had been refused.
 *
 * This keeps the chrome, says what is behind the door, and signs them in
 * without leaving the page — the same inline gate the articles use. The route
 * itself is unchanged: still members-only, still the same check in
 * PrivateRoute. Only the refusal is different.
 */

/* Longest matching prefix wins, so /labs/kubernetes beats /labs. */
const GATE_COPY = {
  '/dashboard':        { icon: '📊', title: 'Your dashboard', desc: 'Your progress, streaks and what to pick up next.' },
  '/my-learning':      { icon: '🎯', title: 'My learning', desc: 'Everything you have started, in one place.' },
  '/labs':             { icon: '🧪', title: 'Hands-on labs', desc: 'Practise on real infrastructure — Kubernetes, cloud and AI agent labs that run in the browser, with no setup.' },
  '/labs/kubernetes':  { icon: '☸️', title: 'Kubernetes labs', desc: 'Over 200 guided exercises, from your first pod to debugging a cluster that will not schedule.' },
  '/labs/local-cloud': { icon: '🌩️', title: 'Local cloud labs', desc: 'Build AWS-shaped infrastructure on your own machine. No account, no bill, no risk of leaving something running.' },
  '/labs/ai-agents':   { icon: '🤖', title: 'AI agent labs', desc: 'Build and debug agents properly: tools, memory, retrieval and evaluation.' },
  '/jobs':             { icon: '💼', title: 'Job listings', desc: 'Thousands of cloud, DevOps, MLOps and platform roles, refreshed daily.' },
  '/courses':          { icon: '🎓', title: 'Courses', desc: 'Structured paths through cloud, DevOps and MLOps, built to be finished rather than collected.' },
  '/courses/new':      { icon: '✨', title: 'AI Course Builder', desc: 'Describe the course you want and your own AI agent researches and writes it — lessons, labs, quizzes and a capstone — in LearnXops Desktop.' },
  '/interview':        { icon: '📚', title: 'Interview question bank', desc: 'Thousands of real interview questions with worked answers, not one-line definitions.' },
  '/virtual-interview': { icon: '🎙️', title: 'Mock interviews', desc: 'Practise out loud with an interviewer that pushes back, then read the transcript.' },
  '/cert-exam':        { icon: '🏅', title: 'Certification exam prep', desc: 'Practice exams that mirror the real thing, marked with explanations.' },
  '/resume-builder':   { icon: '📄', title: 'Resume builder', desc: 'Build a resume that survives a screen, with wording tuned for engineering roles.' },
  '/cover-letter':     { icon: '✉️', title: 'Cover letter builder', desc: 'Draft a letter for a specific role in a couple of minutes.' },
  '/job-tracker':      { icon: '📊', title: 'Job search tracker', desc: 'Every application, where it stands and what is next.' },
  '/projects':         { icon: '🛠️', title: 'Project ideas', desc: 'Portfolio projects worth building, scoped so you can actually finish one.' },
  '/certificates':     { icon: '🏅', title: 'Your certificates', desc: 'Everything you have completed, shareable.' },
  '/leaderboard':      { icon: '🏆', title: 'Leaderboard', desc: 'How you compare with everyone else working through the labs.' },
  // The curated paths are public now; the generator is the only part behind
  // the gate, so the copy describes that rather than the section.
  '/learning-paths/roadmap': { icon: '🗺️', title: 'Build your own roadmap', desc: 'Describe what you want to learn and get a day-by-day plan built around it. The curated paths are free to browse without an account.' },
};
const DEFAULT_COPY = { icon: '🔒', title: 'Members only', desc: 'Create a free account to continue.' };

export function gateCopyFor(pathname) {
  let best = '';
  for (const prefix of Object.keys(GATE_COPY)) {
    if ((pathname === prefix || pathname.startsWith(`${prefix}/`)) && prefix.length > best.length) best = prefix;
  }
  return GATE_COPY[best] || DEFAULT_COPY;
}

export default function MemberGate() {
  const { login } = useAuth();
  const location = useLocation();
  const { icon, title, desc } = gateCopyFor(location.pathname);

  // Signing in sets the user, PrivateRoute re-renders, and the page they
  // originally asked for appears underneath. No redirect, no second hop.
  const onAuthed = useCallback((token, userData) => login(token, userData), [login]);

  // Most of these paths are disallowed in robots.txt already, but not all of
  // them, and a gate is never the page worth indexing.
  useSeo({ title, description: desc, noindex: true, canonical: null });

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 sm:px-6 py-14" style={{ minHeight: '100vh' }}>
        <div className="rounded-2xl p-7 text-center"
          style={{ background: 'var(--lx-card)', border: '1px solid var(--lx-border)' }}>
          <div className="text-4xl mb-3" aria-hidden="true">{icon}</div>
          <h1 className="text-xl font-bold text-white tracking-tight">{title}</h1>
          <p className="text-sm text-gray-400 leading-relaxed mt-2 mb-6">{desc}</p>

          <PostAuthGate onAuthed={onAuthed} redirectPath={location.pathname + location.search} />

          <p className="text-xs text-gray-600 mt-5">
            New to LearnXops?{' '}
            <Link to="/signup" className="text-indigo-400 hover:text-indigo-300">Create a free account</Link>
            {' · '}
            <Link to="/posts" className="text-gray-500 hover:text-gray-300">Browse the free articles</Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
